import React, {useState, useCallback, useEffect, useRef} from 'react';
import {usePreventDragging} from 'components/effects/preventDragging.js';
import {setContextualHelp} from 'actions/index.js';
import { ReactReduxContext, Provider } from 'react-redux';
import MouseSelection from './MouseSelection.js';
import FMOperatorComponent from 'components/instruments/FMOperatorComponent.js';
import EffectComponent from 'components/effects/EffectComponent.js';
import {MIDI_FX, AUDIO_FX, OSCILLATOR} from 'constants/effect-types.js';
import MacrosView from './MacrosView.js';
import SingleLFOComponent from 'components/lfo/SingleLFOComponent.js';
import {uuid} from 'uuidv4';
import {getNonOverlapping} from './overlaps.js';
import {setEffectRackNodePosition} from 'actions/index.js';
import SystemEnvelopeComponent from 'components/envelope/SystemEnvelopeComponent.js';
import MidiEffectComponent from 'components/effects/MidiEffectComponent.js';
import EffectMods from '../EffectMods.js';
import { connect } from "react-redux";
import EdgeDetails from './EdgeDetails.js';
import NodeIOs from './NodeIOs.js';
import {LFO, ENVELOPE} from 'constants/mod-types.js';
import ConditionedComponent from 'components/ConditionedComponent.js';
import RackToolbar from './RackToolbar.js';
import RackBottomBar from './RackBottomBar.js';
import DrawingEdge from './DrawingEdge.js';
import Edges from './Edges.js';
import {useNodeHistory} from './useNodeHistory.js';
import ModulatorEdges from './ModulatorEdges.js';
import MacroEdges from './MacroEdges.js';
import ModulatorComponent from './ModulatorComponent.js';
import '../../../Rack.scss';
import {setCopiedNodesForRack} from 'actions/index.js';
import Edge from './Edge.js';
import {
    INPUT,
    OUTPUT,
    NODE_WIDTH,
    WIDTH,
    HEIGHT,
    NODE_HEIGHT} from './constants.js';
import NodeComponent from './NodeComponent.js';
import MacroComponent from './MacroComponent.js';
import MacroDetails from './MacroDetails.js';

window.rackColor = '#0e24741f';

function RackComponent(props) {
    let rack = props.effect;
    let ref = useRef(undefined);
    let {preventDragging} = usePreventDragging({ref, effect: rack});
    let {routingNumber, macrosDisabled} = props;
    const [selectedEdge, setSelectedEdge] = useState(undefined);
    const [dragging, setDragging] = useState(undefined);
    const [effects, setEffects] = useState(rack.effects);
    const [drawingEdge, setDrawingEdge] = useState(undefined);
    const [edges, setEdges] = useState(rack.adjacencyLists);
    const [lfoEdges, setLfoEdges] = useState(rack.lfoAdjacencyLists);
    const [envelopeEdges, setEnvelopeEdges] = useState(rack.envelopeAdjacencyLists);
    const [macroEdges, setMacroEdges] = useState(rack.macroAdjacencyLists);
    const [modulators, setModulators] = useState(rack.modulators);
    const [macros, setMacros] = useState(rack.macros);
    const [showMacrosView, setShowMacrosView] = useState(rack.macros.length > 0);
    const [mouseSelection, setMouseSelection] = useState(undefined);
    const [selectedNodes, setSelectedNodes] = useState([]);
    const {getLastNode} = useNodeHistory(selectedNodes);
    const {copiedNodes, setCopiedNodes} = props;
    let selectedNode = selectedNodes[0];
    const isDraggable = props.isDraggable === undefined ? true : props.isDraggable;

    useEffect(() => {
        setEdges(rack.adjacencyLists);
    }, [rack.adjacencyLists]);

    let width = rack.getEffectType() === OSCILLATOR ?
        200 : WIDTH;
    let height = rack.getEffectType() === OSCILLATOR ?
        120: HEIGHT;

    useEffect(() => {
        if (props.selectedParameter && props.selectedParameter.context) {
            if (props.selectedParameter.context.effect === rack) {
                setSelectedNodes([props.selectedParameter.context.subDevice]);
            }
            if (rack.effects.includes(props.selectedParameter.isMapped)) {
                setSelectedNodes([props.selectedParameter.isMapped]);
            }
        }
    }, [props.selectedParameter, rack, setSelectedNodes]);

    useEffect(() => {
        setEffects(rack.effects);
    }, [rack.effects, setEffects]);

    const handleChooseEffect = useCallback((effect) => {
        let x1 = 100;
        let y1 = height / 2;
        if (selectedNode && selectedNode.getType === undefined) {
            x1 = selectedNode.x;
            y1 = selectedNode.y;
        }
        let {x, y} = getNonOverlapping(x1, y1, rack);
        rack.addEffect(effect);
        effect.x = x;
        effect.y = y;
        props.setNodePosition(effect.id, x, y);
        setSelectedNodes([effect]);
        setEffects([... rack.effects]);
        setTimeout(() => {
            props.setContextualHelp(effect);
        }, 25);
    }, [setEffects, setSelectedNodes, selectedNode]);

    useEffect(() => {
        rack.setSize(width, height, NODE_WIDTH, NODE_HEIGHT);
        setEffects([... rack.effects]);
    }, [setEffects, rack]);

    useEffect(() => {
        setModulators(rack.modulators);
        setLfoEdges(rack.lfoAdjacencyLists);
        setEnvelopeEdges(rack.envelopeAdjacencyLists);
    }, [rack.loaded, setModulators, setLfoEdges, setEnvelopeEdges]);

    useEffect(() => {
        
    }, [selectedNodes]);
    useEffect(() => {
        if (dragging) {
            if (selectedNodes.indexOf(dragging.effect) === -1) {
                setSelectedNodes([dragging.effect]);
                props.setContextualHelp(dragging.effect);
            }
            setMouseSelection(undefined);
        }
    }, [dragging, setSelectedNodes, setSelectedEdge, selectedNodes, setMouseSelection]);

    useEffect(() => {
        if (props.setSelectedOscillator) {
            props.setSelectedOscillator(selectedNodes[0]);
        }
    }, [selectedNodes]);

    useEffect(() => {
        return () => {
            setCopiedNodes([]);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [
        selectedEdge,
        setSelectedNodes,
        selectedNode,
        setEdges,
        setEffects,
        props.isSelected]);

    const onKeyDown = useCallback((e) => {
        if (rack.getEffectType() === OSCILLATOR &&
            (e.key !== 'Backspace')) {
            return;
        }
        if (e.target.tagName.toUpperCase() === 'INPUT' ||
            (rack.getEffectType() !== OSCILLATOR && !props.isSelected)) {
            return;
        }
        e.stopPropagation();
        if (e.key === 'Backspace') {
            if (selectedEdge) {
                if (rack.getEffectType() === OSCILLATOR &&
                    selectedEdge.modulator === undefined) {
                    return;
                }
                rack.deleteEdge(selectedEdge);
                setEdges({... rack.adjacencyLists});
                setLfoEdges({... rack.lfoAdjacencyLists});
                setEnvelopeEdges({... rack.envelopeAdjacencyLists});
            } else if (selectedNodes.length > 0) {
                if (rack.getEffectType() === OSCILLATOR) {
                    return;
                }
                for (let selectedNode of selectedNodes) {
                    if (rack.isMacro(selectedNode)) {
                        rack.deleteMacro(selectedNode);
                        setMacros([... rack.macros]);
                        setMacroEdges({... rack.macroAdjacencyLists});
                    } else {
                        rack.deleteEffect(selectedNode);
                    }
                }
                props.setContextualHelp(undefined);
                setSelectedNodes([]);
                setLfoEdges({... rack.lfoAdjacencyLists});
                setEnvelopeEdges({... rack.envelopeAdjacencyLists});
                setEdges({... rack.adjacencyLists});
                setEffects([... rack.effects]);
            }
        } else if (e.key === 'l' && (e.metaKey || e.ctrlKey)) {
            let node = getLastNode(selectedNodes);
            if (node && selectedNodes[0]) {
                rack.connectNodes(node, selectedNodes[0]);
                setEdges({... rack.adjacencyLists});
            }
        } else if (e.key === 'd' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            e.stopPropagation();

            if (selectedNode && selectedNode.getType === undefined) {
                // it's an effect so we duplicate
                let effect = selectedNode.getEffectCopy();
                effect.id = uuid();
                rack.addEffect(effect);
                let {x, y} = getNonOverlapping(selectedNode.x, selectedNode.y, rack);
                effect.x = x;
                effect.y = y;
                setEffects([... rack.effects]);
            }
            return false;
        } else if (e.key === 'c' && (e.metaKey || e.ctrlKey)) {
            e.stopPropagation();
            e.preventDefault();
            setCopiedNodes([... selectedNodes]);
            return false;
            // copy selected nodes along with their structure
        } else if (e.key === 'v' && (e.metaKey || e.ctrlKey)) {
            // paste selected nodes along with their structure
            e.stopPropagation();
            e.preventDefault();
            rack.pasteNodes(props.sequencerEngine.state.effects.copiedNodesForRack);
            setEffects([... rack.effects]);
            setEdges({... rack.adjacencyLists});
            return false;
        } else if (e.key === 'o' && (e.metaKey || e.ctrlKey)) {
            e.stopPropagation();
            e.preventDefault();

            selectedNodes.forEach(
                node => connectEdge(node, OUTPUT, {mode: INPUT, node: rack.outputNode}));

            return false;
        } else if (e.key === 'i' && (e.metaKey || e.ctrlKey)) {
            e.stopPropagation();
            e.preventDefault();

            selectedNodes.forEach(
                node => connectEdge(node, INPUT, {mode: OUTPUT, node: rack.inputNode}));

            return false;
        }
    }, [
        connectEdge,
        props.isSelected,
        setCopiedNodes,
        copiedNodes,
        selectedEdge,
        setSelectedNodes,
        selectedNodes,
        setEdges,
        setEffects,
        setLfoEdges,
        setEnvelopeEdges]);

    useEffect(() => {
        if (selectedNode) {
            setSelectedEdge(undefined);
        }
    }, [selectedNode, setSelectedEdge]);

    useEffect(() => {
        if (selectedEdge) {
            props.setContextualHelp(undefined);
            setSelectedNodes([]);
        }
    }, [selectedEdge, setSelectedNodes]);

    const getPoints = (e) => {
        let rect = ref.current.getBoundingClientRect();
        
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    }

    const onMouseDown = useCallback((e) => {
        if (!props.isSelected) {
            props.sequencerEngine.selectEffect(rack);
        }
        if (isDraggable) {
            preventDragging(false);
        }
        let pts = getPoints(e);
        let x = pts.x;
        let y = pts.y;
        setMouseSelection({x1: x, y1: y, x2: x, y2: 2});
    }, [setMouseSelection, props.isSelected, rack]);

    const onMouseMove = useCallback((e) => {
        if (dragging) {
            let pts = getPoints(e);
            let x = pts.x - dragging.x;
            let y = pts.y - dragging.y;
            let {effect} = dragging;
            effect.x = x;
            effect.y = y;
            props.setNodePosition(effect.id, x, y);

            for (let node of selectedNodes) {
                if (node === effect) {
                    continue;
                }

                if (node._x === undefined) {
                    node._x = node.x;
                } 
                if (node._y === undefined) {
                    node._y = node.y;
                } 
                let _x = node._x + (x - dragging.origX);
                let _y = node._y + (y - dragging.origY);
                node.x = _x;
                node.y = _y;
                props.setNodePosition(node.id, _x, _y);
            }
            setMouseSelection(undefined);
            return;
        }

        if (drawingEdge) {
            let pts = getPoints(e);
            setDrawingEdge({
                ... drawingEdge,
                x2: pts.x,
                y2: pts.y
            });
            setMouseSelection(undefined);
            return;
        }
        
        if (mouseSelection) {
            let pts = getPoints(e);
            setMouseSelection(
                {... mouseSelection,
                 x2: pts.x,
                 y2: pts.y
                });
        }
    }, [
        dragging,
        effects,
        selectedNodes,
        setEffects,
        drawingEdge,
        setDrawingEdge,
        mouseSelection,
        setMouseSelection
    ]);

    const onMouseUp = useCallback((e) => {
        if (isDraggable) {
            preventDragging(true);
        }
        setDragging(undefined);
        setDrawingEdge(undefined);
        if (mouseSelection) {
            // find all effects that match
            let {x1, x2, y1, y2} = mouseSelection;
            let selectedNodes = 
                rack.effects.filter(
                    ({x, y}) =>
                        x >= x1 && x <= x2 &&
                        y >= y1 && y <= y2
                );
            selectedNodes.forEach(node => {
                node._x = undefined;
                node._y = undefined;
            });
            props.setContextualHelp(selectedNodes[0]);
            setSelectedNodes(selectedNodes);
            setMouseSelection(undefined);
        }

        selectedNodes.forEach(node => {
            node._x = undefined;
            node._y = undefined;
        });

        if (!props.isSelected) {
            props.sequencerEngine.selectEffect(rack);
        }
    }, [setDragging, setDrawingEdge, setMouseSelection, mouseSelection, setSelectedNodes, rack, selectedNodes, props.isSelected]);

    const startDrawingEdge = useCallback((node, mode, x, y, outputNumber=0) => {
        setDrawingEdge({node, mode, x1: x, y1: y, x2: x, y2: y, outputNumber});
        setSelectedEdge(undefined);
    }, [setDrawingEdge, setSelectedEdge]);

    const connectEdge = useCallback((node, mode, edge=drawingEdge) => {
        if (edge === undefined) {
            return;
        }
        if (mode === INPUT) {
            if (edge.mode === INPUT) {
                return;
            }
            rack.connectNodes(edge.node, node, edge.outputNumber);
        } else {
            if (edge.mode === OUTPUT) {
                return;
            }
            rack.connectNodes(node, edge.node, edge.outputNumber);
        }
        setEdges({... rack.adjacencyLists});
        setLfoEdges({... rack.lfoAdjacencyLists});
        setEnvelopeEdges({... rack.envelopeAdjacencyLists});
        setMacroEdges({... rack.macroAdjacencyLists});
    }, [drawingEdge, setEdges, setEnvelopeEdges, setLfoEdges, setMacroEdges]);

    let effectComponent = '';

    if (!selectedEdge && rack.getEffectType() === OSCILLATOR) {
        effectComponent = (
            <FMOperatorComponent
              color={props.color}
              oscillator={selectedNode}
              setSelectedNode={(node) => node ? setSelectedNodes([node]) : setSelectedNodes([])}
              instrument={rack}
              sequencerEngine={props.sequencerEngine}
            />
        );
    } 

    if (selectedNode) {
        if (rack.isMacro(selectedNode)) {
            effectComponent = (
                <MacroDetails
                  sequencerEngine={props.sequencerEngine}
                  macro={selectedNode}/>
            );
        } else if (selectedNode.getType) {
            if (selectedNode.getType() === LFO) {
                effectComponent = (
                    <SingleLFOComponent
                      hideSelection={true}
                      sequencerEngine={props.sequencerEngine}
                      lfo={selectedNode.context.lfoTrack}/>
                );
            } else {
                effectComponent = (
                    <SystemEnvelopeComponent
                      hideSelection={true}
                      sequencerEngine={props.sequencerEngine}
                      envelopeTrack={selectedNode.context.envelopeTrack}/>
                );
            }
        } else if (selectedNode && selectedNode.getEffectType) {
            if (selectedNode.getEffectType() === MIDI_FX) {
                effectComponent = (
                    <MidiEffectComponent
                      isPulled={props.pulledEffects.includes(selectedNode)}
                      hideBarButtons={true}
                      sequencerEngine={props.sequencerEngine}
                      onDeleteEffect={() => []}
                      scrollContainer={props.scrollContainer}
                      effect={selectedNode}
                      track={props.track}
                      send={props.send}
                    />);
            } else if (selectedNode.getEffectType() === AUDIO_FX) {
                effectComponent = (
                    <EffectComponent
                      isPulled={props.pulledEffects.includes(selectedNode)}
                      hideBarButtons={true}
                      sequencerEngine={props.sequencerEngine}
                      onDeleteEffect={() => []}
                      scrollContainer={props.scrollContainer}
                      effect={selectedNode}
                      track={props.track}
                      send={props.send}
                    />);
            }
        }
    }

    if (selectedEdge) {
        if (selectedEdge.effect) {
            effectComponent = (
                <EdgeDetails
                  rack={rack}
                  edge={selectedEdge} sequencerEngine={props.sequencerEngine}/>
            );
        } else if (selectedEdge.modulator) {
            let parameter = selectedEdge.edge;
            effectComponent = (
                <div className="edge-properties">
                  <div>Parameter Mod Properties: </div>
                  <EffectMods parameters={[parameter]}/>
                </div>
            );
        }
    }

    let nodePropsToPass = {
        setSelectedEdge,
        setSelectedNodes,
        selectedEdge,
        setDragging,
        rack,
        startDrawingEdge,
        drawingEdge,
        connectEdge
    };

    if (showMacrosView) {
        return (
            <MacrosView
              macros={macros}
              setShowMacrosView={setShowMacrosView}
              sequencerEngine={props.sequencerEngine}
            />
        );
    }

    if (!edges || !effects) {
        return [];
    }

    return (
        <div
          onMouseLeave={() => setContextualHelp(undefined)}
          className="effect-rack">
          <RackToolbar
            macrosDisabled={macrosDisabled}
            setEdges={setEdges}
            selectedNodes={selectedNodes}
            rack={rack}
            setMacros={setMacros}
            sequencerEngine={props.sequencerEngine}
            setShowMacrosView={setShowMacrosView}
            handleChooseEffect={handleChooseEffect}/>
          <div className="horizontal">
            <div className="draw-component">
              <svg
                className="rack-canvas"
                ref={ref}
                width={width} height={height}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}>
                <Provider store={props.sequencerEngine.store}>
                  <g>
                    <ModulatorEdges
                      width={width}
                      envelopeEdges={envelopeEdges}
                      lfoEdges={lfoEdges}
                      rack={rack}
                      setSelectedEdge={setSelectedEdge}
                      selectedEdge={selectedEdge}/>
                    <MacroEdges
                      macroEdges={macroEdges}
                      rack={rack}
                      setSelectedEdge={setSelectedEdge}
                      selectedEdge={selectedEdge}/>
                    {modulators.map(
                        (mod, i) =>
                            <ModulatorComponent
                              number={i}
                              key={i}
                              {... nodePropsToPass}
                              width={width}
                              rack={rack}
                              height={height}
                              modulator={mod}
                              selected={selectedNode === mod}
                              numberOfModulators={modulators.length}/> )}
                    {macros.map(
                        (macro, i) =>
                            <MacroComponent
                              rackRef={ref}
                              key={i}
                              number={i}
                              {... nodePropsToPass}
                              width={WIDTH}
                              rack={rack}
                              height={HEIGHT}
                              macro={macro}
                              selected={selectedNode === macro}
                              numberOfModulators={modulators.length}/> )}
                    <Edges
                      edges={edges}
                      rack={rack}
                      isMidiEffect={rack.getEffectType() === MIDI_FX}
                      startDrawingEdge={startDrawingEdge}
                      setSelectedEdge={setSelectedEdge}
                      selectedEdge={selectedEdge}/>
                    {effects
                     .filter(node => selectedNodes.indexOf(node) > -1)
                     .map(
                         (effect, i) =>
                             <NodeComponent
                               {... nodePropsToPass}
                               rackRef={ref}
                               selected={true}
                               key={'node-' + effect.id + '-' + i}
                               rack={rack}
                               edges={edges[effect.id]}
                               effect={effect}
                             />)}
                  </g>
                  <g>
                    {effects
                     .filter(node => selectedNodes.indexOf(node) === -1)
                     .map((effect, i) =>
                          <NodeComponent
                            {... nodePropsToPass}
                            rackRef={ref}
                            color={props.color}
                            selected={false}
                            key={'node-' + effect.id}
                            rack={rack}
                            edges={edges[effect.id]}
                            effect={effect}
                          />)}
                  </g>
                 <g>
                    <DrawingEdge drawingEdge={drawingEdge}/>
                    {macros.map(macro =>
                                <NodeIOs
                                  key={'macro-' + macro.id}
                                  setSelectedEdge={setSelectedEdge}
                                  selectedEdge={selectedEdge}
                                  rack={rack}
                                  setDragging={setDragging}
                                  startDrawingEdge={startDrawingEdge}
                                  drawingEdge={drawingEdge}
                                  connectEdge={connectEdge}
                                  edges={macroEdges[macro.id]}
                                  effect={macro}
                                />)}
                    
                    {effects
                     .filter(effect => selectedNode === effect)
                     .map(
                         (effect, i) =>
                             <NodeIOs
                               key={'node-' + effect.id}
                               setSelectedEdge={setSelectedEdge}
                               selectedEdge={selectedEdge}
                               rack={rack}
                               setDragging={setDragging}
                               startDrawingEdge={startDrawingEdge}
                               drawingEdge={drawingEdge}
                               connectEdge={connectEdge}
                               edges={edges[effect.id]}
                               effect={effect}
                             />)}
                    {effects
                     .filter(effect => selectedNode !== effect)
                     .map(
                         (effect, i) =>
                             <NodeIOs
                               key={'node-' + effect.id}
                               setSelectedEdge={setSelectedEdge}
                               selectedEdge={selectedEdge}
                               rack={rack}
                               setDragging={setDragging}
                               startDrawingEdge={startDrawingEdge}
                               drawingEdge={drawingEdge}
                               connectEdge={connectEdge}
                               edges={edges[effect.id]}
                               effect={effect}
                             />)}
                    {rack.getEffectType() !== OSCILLATOR ?
                     <NodeComponent
                       hasOutput={true}
                       effect={rack.inputNode}
                       rackRef={ref}
                       rack={rack}
                       text="Input"
                       x={0}
                       startDrawingEdge={startDrawingEdge}
                       setSelectedEdge={setSelectedEdge}
                       selectedEdge={selectedEdge}
                       edges={edges[-1]}
                       connectEdge={connectEdge}
                       y={height / 2 - NODE_HEIGHT / 2}
                     /> : []}
                    {rack.outputNodeX ?
                     [(
                         <NodeComponent
                           key={1}
                           hasInput={true}
                           color={props.color}
                           rackRef={ref}
                           text="X"
                           startDrawingEdge={startDrawingEdge}
                           setSelectedEdge={setSelectedEdge}
                           rack={rack}
                           selectedEdge={selectedEdge}
                           effect={rack.outputNodeX}
                           x={width-32}
                           connectEdge={connectEdge}
                           y={40}
                         />
                     ),
                      (
                          <NodeComponent
                           key={2}
                            hasInput={true}
                            text="Y"
                            color={props.color}
                            rackRef={ref}
                            startDrawingEdge={startDrawingEdge}
                            setSelectedEdge={setSelectedEdge}
                            rack={rack}
                            selectedEdge={selectedEdge}
                            effect={rack.outputNodeY}
                            x={width-32}
                            connectEdge={connectEdge}
                            y={80}
                          />
                      )
                     ] : 
                     <NodeComponent
                       hasInput={true}
                       text="Output"
                       rackRef={ref}
                       startDrawingEdge={startDrawingEdge}
                       key={3}
                       setSelectedEdge={setSelectedEdge}
                       rack={rack}
                       selectedEdge={selectedEdge}
                       effect={rack.outputNode}
                       x={width-NODE_WIDTH}
                       connectEdge={connectEdge}
                       y={height / 2 - NODE_HEIGHT / 2}
                     /> }
                    <MouseSelection mouseSelection={mouseSelection}/>
                  </g>
                </Provider>
              </svg>
            </div>
            {effectComponent}
          </div>
          <RackBottomBar
            routingNumber={routingNumber}
            rack={rack}
          />
        </div>
    );
}

const mapStateToProps = (state, props) => {
    return {
        loaded: props.effect.loaded,
        isSelected: state.effects.selectedEffect  === props.effect.id,
        routingNumber: props.effect.currentConfiguration,
        algorithm: props.effect.algorithmParameter &&
            props.effect.algorithmParameter.get(),
        copiedNodes: state.effects.copiedNodesForRack,
        numEffects: props.effect && props.effect.effects ? props.effect.effects.length : 0,
        effects: props.effect.effects,
        adjacencyLists: props.effect.adjacencyLists,
        selectedParameter: state.effects.accumulatorParameterSelected,
        isSelected: state.effects.selectedEffect === props.effect.id,
        pulledEffects: state.effects.pulledEffects
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        setNodePosition: (effectId, x, y) => dispatch(setEffectRackNodePosition(props.effect.id, effectId, x, y)),
        setCopiedNodes: (nodes) => dispatch(setCopiedNodesForRack(nodes)),
        setContextualHelp: (e) => dispatch(setContextualHelp(e)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RackComponent);


